// CRM entity types, hooks, and callables for WaTag:
// Labels (tenants/{t}/labels), Lists (tenants/{t}/lists), and Custom Fields (tenants/{t}/customFields).
// Full contract matches docs/agents/WP-03-backend/report.md.

import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { stripUndefined } from './callable';
import { useTenant } from './tenant';

export interface LabelDoc {
  id: string;
  name: string;
  color: string;
  createdAt: number;
  updatedAt: number;
}

export interface ListDoc {
  id: string;
  name: string;
  description?: string;
  count: number;
  createdAt: number;
  updatedAt: number;
}

export type CustomFieldType = 'text' | 'number' | 'date' | 'boolean' | 'select';

export interface CustomFieldDoc {
  id: string;
  key: string;
  label: string;
  type: CustomFieldType;
  options?: string[];
  createdAt: number;
  updatedAt: number;
}

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => (await fn(stripUndefined(data))).data;
}

// ── Callables ─────────────────────────────────────────────────────────────────

export const labelSave = call<
  { id?: string; name: string; color: string },
  { ok: true; label: LabelDoc }
>('labelSave');

export const labelDelete = call<
  { id: string },
  { ok: true; cleanedSubscribers: number }
>('labelDelete');

export const labelList = call<Record<string, never>, { labels: LabelDoc[] }>('labelList');

export const migrateLabels = call<
  Record<string, never>,
  { ok: true; migratedCount: number; createdLabelsCount: number; totalSubscribersScanned: number }
>('migrateLabels');

export const listSave = call<
  { id?: string; name: string; description?: string },
  { ok: true; list: ListDoc }
>('listSave');

export const listDelete = call<
  { id: string },
  { ok: true; cleanedSubscribers: number }
>('listDelete');

export const listList = call<Record<string, never>, { lists: ListDoc[] }>('listList');

export const customFieldSave = call<
  { id?: string; key: string; label: string; type: CustomFieldType; options?: string[] },
  { ok: true; customField: CustomFieldDoc }
>('customFieldSave');

export const customFieldDelete = call<
  { id: string },
  { ok: true; cleanedSubscribers: number }
>('customFieldDelete');

export const customFieldList = call<
  Record<string, never>,
  { customFields: CustomFieldDoc[] }
>('customFieldList');

// ── Real-Time Hooks ──────────────────────────────────────────────────────────

export function useLabels() {
  const { tenantId } = useTenant();
  const [labels, setLabels] = useState<LabelDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLabels([]);
      setLoading(false);
      return;
    }

    const q = query(collection(db, `tenants/${tenantId}/labels`), orderBy('name', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        setLabels(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<LabelDoc, 'id'>),
          })),
        );
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [tenantId]);

  return { labels, loading, error };
}

export function useLists() {
  const { tenantId } = useTenant();
  const [lists, setLists] = useState<ListDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLists([]);
      setLoading(false);
      return;
    }

    const q = query(collection(db, `tenants/${tenantId}/lists`), orderBy('name', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        setLists(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<ListDoc, 'id'>),
          })),
        );
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [tenantId]);

  return { lists, loading, error };
}

export function useCustomFields() {
  const { tenantId } = useTenant();
  const [customFields, setCustomFields] = useState<CustomFieldDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setCustomFields([]);
      setLoading(false);
      return;
    }

    const q = query(collection(db, `tenants/${tenantId}/customFields`), orderBy('key', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        setCustomFields(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<CustomFieldDoc, 'id'>),
          })),
        );
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [tenantId]);

  return { customFields, loading, error };
}
